/**
 * GPU Detail Adapter — GpuMonitoring → GpuDetail SlideDetail 통합.
 *
 * host adapter 패턴(`hostDetail.adapter.ts`)의 슬림 버전.
 *  - SlideDetail store에 `TOTAL_DETAIL.GPU_DETAIL` 타입 panel 등록
 *  - registry: `common/define/slideDetail.define.ts` → `gpuView/gpuDetail/GpuDetail.vue`
 *  - 권한(`MONITORING_GPU_MONITORING` = GPU 도메인 그룹) + zod 파라미터 검증 + target 유효성(GPU targetKind) 적용
 *
 * 대시보드 위젯 연계 (연결됨): BE `DataTriggerTriggerPath`에 gpuInfo/migInfo가 추가되어,
 *  useDashboardDetailAdapter.routeToAdapter의 case 'gpuInfo'/'migInfo'로 배선됨. 두 진입 경로가 있다.
 *  - status 헥사 위젯 GPU/MIG 헥사 클릭(DSP-40850): 클릭 행에서 파라미터를 만들어 openGpuDetail 직행
 *    (MIG 는 부모 GPU 로 리졸브, `buildGpuDetailParamsFromRow`).
 *  - UUID-only 트리거(메트릭 위젯 등): openGpuDetailByTrigger가 mig-info API(gpuUuids/migUuids 공용)로
 *    serverId/serverType·부모 GPU를 조회하고, from/toTime은 패널로 전달한다.
 */
import { ref } from 'vue';
import { z } from 'zod';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { getV11InfraGpuMigInfo } from '@/api-client/infra/queries/infra-gpu-controller/infra-gpu-controller';
import type { InfraGpuMigInfoResponse } from '@/api-client/infra/model';

/**
 * 디테일 호출 시 받는 식별자.
 * `SlideDetailAdapterParams<T>`의 제약(`Record<string, unknown>`)은 z.input 객체 타입으로 충족.
 *  - serverId: GPU가 속한 서버 ID(server.serverId, 필수 — panel host). serverType이 'host'면 hostId, 'node'면 nodeId.
 *  - gpuUid: GPU UUID — target 유효성/정보 조회 1순위 식별자.
 *  - serverType: 'node' | 'host' — gpu/info 조회 파라미터.
 *  - name: 슬라이드 타이틀 값(GPU name). 없으면 gpuModel → serverId/gpuIndex fallback.
 *  - selectedMigUid: 메인 뷰 MIG 헥사 클릭 진입 시 디테일 MIG 섹션에서 선택할 MIG UUID.
 *  - fromTime/toTime: 대시보드 위젯 연계 시 조회 시간 범위(UTC-0). 패널 addition으로 전달(삭제 타겟 timestamp 보존).
 */
const gpuDetailInputSchema = z.object({
  serverId: z.string().min(1),
  gpuUid: z.string().optional(),
  gpuIndex: z.number().optional(),
  gpuModel: z.string().optional(),
  serverType: z.string().optional(),
  name: z.string().optional(),
  selectedMigUid: z.string().optional(),
  fromTime: z.string().optional(),
  toTime: z.string().optional(),
});

export type GpuDetailInput = z.input<typeof gpuDetailInputSchema>;

/**
 * 대시보드 트리거(gpuInfo/migInfo) 진입용 식별자 — targetId(gpuUuid/migUuid)만 넘어온다.
 * serverId/serverType·부모 GPU는 mig-info API로 조회한다. type alias여야 `Record<string, unknown>` 제약 충족.
 */
export type GpuTriggerInput = {
  gpuUuid?: string;
  migUuid?: string;
  name?: string;
  fromTime?: string;
  toTime?: string;
};

export const useGpuDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const DEFAULT_ROLE_PERMISSION_KEY = ROLE_PERMISSION_KEY.MONITORING.MONITORING_GPU_MONITORING;

  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  /**
   * GPU 디테일 슬라이드 열기 (표준 SlideDetailAdapter 시그니처).
   * 권한 → zod 파라미터 검증 → target 유효성(gpuUid, GPU targetKind) → 패널 오픈.
   */
  const openGpuDetail = async ({
    params,
    options,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<GpuDetailInput>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
    } = options ?? {};

    // 1. 권한 검증 — GPU 모니터링 권한 없으면 슬라이드 미오픈
    if (
      runCheckPermission &&
      isDetailSlidePermissionDenied({
        rolePermissionKey: rolePermissionKey ?? DEFAULT_ROLE_PERMISSION_KEY,
        type: TOTAL_DETAIL.GPU_DETAIL,
        showError: showErrorMessage,
      })
    ) {
      return createSlideDetailResult('permission_denied');
    }

    // 2. 파라미터 검증 — serverId 필수(panel host)
    const parsed = gpuDetailInputSchema.safeParse(params);
    if (!parsed.success) {
      if (showErrorMessage) {
        const missingParams = parsed.error.issues.map((issue) => issue.path[0]).join(', ');
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', { value: missingParams }),
        });
      }
      return createSlideDetailResult('invalid_params');
    }

    // 3. 타겟 유효성 검증 — GPU targetKind. gpuUid가 있을 때만(없으면 검증할 target 없음).
    if (runTargetValidation && parsed.data.gpuUid) {
      const targetValidation = await checkIsValidTarget(
        { targetId: parsed.data.gpuUid, targetKind: TargetStatusRequestTargetKind.GPU },
        { showErrorMessage },
      );
      if (!targetValidation.valid) {
        return createSlideDetailResult(targetValidation.reason);
      }
    }

    // 4. 탭 패널 열기
    const p = parsed.data;
    const displayName =
      p.name ?? p.gpuModel ?? `${p.serverId}${p.gpuIndex !== undefined ? `/${p.gpuIndex}` : ''}`;

    const result = addTabPanel(
      {
        type: TOTAL_DETAIL.GPU_DETAIL,
        host: p.serverId,
        name: displayName,
        addition: {
          gpuUid: p.gpuUid,
          gpuIndex: p.gpuIndex,
          gpuModel: p.gpuModel,
          serverType: p.serverType,
          selectedMigUid: p.selectedMigUid,
          fromTime: p.fromTime,
          toTime: p.toTime,
        },
        state,
        init,
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  /** mig-info API로 gpuUuid/migUuid → 부모 GPU(gpuUuid/serverId/serverType) 매핑 resolve. */
  const resolveGpuMapping = async (reqParams: {
    gpuUuids?: string[];
    migUuids?: string[];
  }): Promise<InfraGpuMigInfoResponse | undefined> => {
    try {
      const { data } = await getV11InfraGpuMigInfo(reqParams);
      return data?.data?.[0];
    } catch (e) {
      console.error('GPU-MIG 매핑 조회 실패:', e);
      return undefined;
    }
  };

  /**
   * 대시보드 트리거 진입 (gpuInfo/migInfo 공용) — 같은 mig-info API로 부모 GPU를 resolve한다.
   *  - gpuUuid 진입: 그 GPU로 열기.
   *  - migUuid 진입: 부모 GPU를 resolve해 열고 해당 MIG를 선택 상태(selectedMigUid)로.
   * serverId/serverType·부모 gpuUuid는 트리거에 없어 API로 조회, from/toTime은 패널로 전달.
   */
  const openGpuDetailByTrigger = async ({
    params,
    options,
    currentState,
    init,
  }: SlideDetailAdapterParams<GpuTriggerInput>): Promise<SlideDetailAdapterResult> => {
    const { gpuUuid, migUuid, name, fromTime, toTime } = params;
    const reqParams = migUuid
      ? { migUuids: [migUuid] }
      : gpuUuid
        ? { gpuUuids: [gpuUuid] }
        : undefined;
    if (!reqParams) return createSlideDetailResult('invalid_params');

    const mapping = await resolveGpuMapping(reqParams);
    // 파라미터 식별자는 uniqueUuid 우선 — mig-info가 resolve해 준 gpuUniqueUuid(없으면 gpuUuid/입력값).
    const gpuUid = mapping?.gpuUniqueUuid ?? mapping?.gpuUuid ?? gpuUuid;
    // 클릭한 MIG의 uniqueUuid — 응답 migUuids/migUniqueUuids 병렬 배열에서 매칭(없으면 migUuid 폴백).
    const migIdx = migUuid ? (mapping?.migUuids?.indexOf(migUuid) ?? -1) : -1;
    const resolvedMigUid = migIdx >= 0 ? (mapping?.migUniqueUuids?.[migIdx] ?? migUuid) : migUuid;
    // serverType까지 있어야 gpu/info 조회 파라미터가 성립 — 없으면 빈 디테일이 되므로 열지 않는다.
    if (!mapping?.serverId || !mapping.serverType || !gpuUid) {
      return createSlideDetailResult('invalid_params');
    }

    return openGpuDetail({
      params: {
        serverId: mapping.serverId,
        gpuUid,
        serverType: mapping.serverType,
        selectedMigUid: resolvedMigUid,
        name,
        fromTime,
        toTime,
      },
      options,
      currentState,
      init,
    });
  };

  return {
    isSlideDetailOpened,
    openGpuDetail,
    openGpuDetailByTrigger,
  };
};
